/**
 * O3 (Salon Owner localization & polish) — owner-portal strings for the
 * "staffSettings" area. Merged into the runtime dictionary by i18n/owner/index.ts and read
 * through LanguageContext's t(). Every key needs BOTH an en and an ar value.
 *
 * Prefixes: `ost.` Team / Manage Staff, `opay.` Payments, `oset.` Settings,
 * `onot.` Notifications. (The inline dictionary already owns `staff.` /
 * `settings.` / `payments.`; keys from those families defined here are
 * deliberate overrides of a missing or wrong value.)
 */
import type { OwnerStrings } from './types';

export const staffSettingsStrings: OwnerStrings = {
    en: {
        /* ── Team / Manage Staff ─────────────────────────────────────── */
        'ost.title': 'Manage staff',
        'ost.subtitle': 'Add your team — stylists, therapists, receptionists, anyone who takes bookings or has their own calendar.',
        'ost.tab.list': 'Staff list',
        'ost.tab.schedule': 'Schedule',
        'ost.tab.inactive': 'Inactive',
        'ost.tabs.aria': 'Staff sections',
        'ost.empty.headline': "You haven't added any team members yet",
        'ost.empty.description': 'Add the people who work at your salon. You can do this any time — team members can also be invited so they can manage their own bookings in the mobile app.',
        'ost.empty.cta': 'Add your first staff member',
        'ost.empty.noMatchFilters': 'No staff match these filters.',
        'ost.empty.noMatchTab': 'No staff in this tab.',
        'ost.benefit.schedule.title': 'Their own schedule',
        'ost.benefit.schedule.desc': 'Each staff member gets their own calendar, working hours and time-off planner.',
        'ost.benefit.services.title': 'Assign services',
        'ost.benefit.services.desc': 'Decide who can perform which services — customers can choose their preferred stylist.',
        'ost.benefit.app.title': 'Mobile staff app',
        'ost.benefit.app.desc': 'Invite them to the Devanza staff app — they manage their own bookings on the go.',
        'ost.error.load': "Couldn't load your staff. Please try again.",
        'ost.error.noPermissionAdd': "You don't have permission to add staff.",
        'ost.error.saveServices': "Couldn't save the services for this staff member.",
        'ost.error.saveBranches': "Couldn't save the branches for this staff member.",
        'ost.error.save': "Couldn't save this staff member. Please try again.",
        'ost.error.delete': "Couldn't delete this staff member. Please try again.",
        'ost.toast.updated': 'Staff member updated.',
        'ost.toast.deleted': 'Staff member deleted.',
        'ost.staffFallbackName': 'Staff member #{id}',
        'ost.role.manager': 'Manager',
        'ost.canPerform': 'Can perform',
        'ost.capability.any': 'Any active service',
        'ost.capability.anyHint': 'No services picked yet, so this staff member can be booked for any in-salon service.',
        'ost.capability.countOne': '1 service',
        'ost.capability.count': '{count} services',
        'ost.quickBook': 'Quick book +',
        'ost.action.edit': 'Edit',
        'ost.action.editNamed': 'Edit {name}',
        'ost.action.delete': 'Delete',
        'ost.action.deleteNamed': 'Delete {name}',
        'ost.count.staff': '{count} staff',
        'ost.count.active': '{count} active',
        'ost.count.inactive': '{count} inactive',
        'ost.count.pending': '{count} pending',
        'ost.addStaff': '+ Add staff',
        'ost.search.placeholder': 'Search by name, email or phone…',
        'ost.search.aria': 'Search staff',
        'ost.filter.branch': 'Branch',
        'ost.filter.branchAria': 'Filter by branch',
        'ost.filter.all': 'All',
        'ost.filter.serviceAria': 'Filter by service',
        'ost.filter.any': 'Any',
        'ost.filter.clearAll': 'Clear all',
        'ost.group.pending': 'Pending invitations',
        'ost.group.active': 'Active team',
        'ost.working': 'Working…',
        'ost.delete.message': "If this staff member has upcoming appointments, you'll need to reassign them. This can't be undone.",
        'ost.invite.pending': 'Pending',
        'ost.invite.expired': 'Expired',
        'ost.invite.expiresInDay': 'Expires in 1 day',
        'ost.invite.expiresInDays': 'Expires in {count} days',
        'ost.invite.expiresInHours': 'Expires in {count} h',
        'ost.invite.expiresSoon': 'Expires soon',
        'ost.invite.copyLink': 'Copy invite link',
        'ost.invite.copyLinkFor': 'Copy invite link for {name}',
        'ost.invite.copied': 'Copied',
        'ost.invite.linkCopied': 'Invite link copied',
        'ost.invite.copyFailed': "Couldn't copy the invite link. Please try again.",
        'ost.invite.resend': 'Resend',
        'ost.invite.resendTo': 'Resend invitation to {email}',
        'ost.invite.resent': 'Invitation sent again to {email}.',
        'ost.invite.resendFailed': "Couldn't resend the invitation.",
        'ost.invite.revoke': 'Revoke',
        'ost.invite.revokeTo': 'Revoke invitation to {email}',
        'ost.invite.revokeConfirm': 'Revoke the invitation sent to {email}? The link in the email will stop working.',
        'ost.invite.revoked': 'Invitation revoked.',
        'ost.invite.revokeFailed': "Couldn't revoke the invitation.",

        'ost.day.0': 'Sunday',
        'ost.day.1': 'Monday',
        'ost.day.2': 'Tuesday',
        'ost.day.3': 'Wednesday',
        'ost.day.4': 'Thursday',
        'ost.day.5': 'Friday',
        'ost.day.6': 'Saturday',

        /* Staff modal */
        'ost.modal.close': 'Close',
        'ost.modal.addTitle': 'Add staff',
        'ost.modal.editTitle': 'Edit staff',
        'ost.modal.editSubtitle': 'Update name, contact details, role and which services they can perform.',
        'ost.modal.about': 'About',
        'ost.modal.photo': 'Profile photo',
        'ost.modal.photoAddHint': 'You can add a photo after saving this staff member.',
        'ost.modal.changePhoto': 'Change photo',
        'ost.modal.uploadPhoto': 'Upload photo',
        'ost.modal.photoFailed': "Couldn't upload the photo. Please try again.",
        'ost.modal.photoHint': 'JPG, PNG or GIF, up to 5 MB. Shown to customers in the booking app.',
        'ost.modal.fullName': 'Full name',
        'ost.modal.fullNamePlaceholder': 'e.g. Sarah Al-Hashemi',
        'ost.modal.email': 'Email',
        'ost.modal.phone': 'Phone number',
        'ost.modal.position': 'Position',
        'ost.modal.positionPlaceholder': 'Stylist, therapist, manager…',
        'ost.modal.role': 'Role',
        'ost.modal.role.staff': 'Staff',
        'ost.modal.role.manager': 'Staff manager',
        'ost.modal.activeHint': "Inactive staff don't appear when customers book.",
        'ost.modal.whatTheyDo': 'What they do',
        'ost.modal.worksAt': 'Works at branches',
        'ost.modal.branchesNone': 'None picked — available at all active branches. Pick branches to narrow it down.',
        'ost.modal.branchesPickedOne': '1 branch picked.',
        'ost.modal.branchesPicked': '{count} branches picked.',
        'ost.modal.hq': 'HQ',
        'ost.modal.services': 'Services this staff member can perform',
        'ost.modal.servicesNone': 'None picked — can be booked for any in-salon service. Pick services to narrow it down.',
        'ost.modal.servicesNoneAllBranches': 'None picked — can be booked for any service at the branches they work at. Pick services to narrow it down.',
        'ost.modal.servicesNonePickedBranches': 'None picked — can be booked for any service at the picked branches.',
        'ost.modal.servicesPickedOne': '1 service picked.',
        'ost.modal.servicesPicked': '{count} services picked.',
        'ost.modal.noServices': 'No services yet. Add services in My Business → Services, then come back to assign them.',
        'ost.modal.pickBranchFirst': 'Pick a branch under "Works at branches" to see the services available there.',
        'ost.modal.noServicesAtBranches': 'There are no services at the picked branches yet. Add services in My Business → Services, then come back to assign them.',
        'ost.modal.searchServices': 'Search {count} services…',
        'ost.modal.searchServicesAria': 'Search services',
        'ost.modal.noMatch': 'No services match "{query}".',
        'ost.modal.clearSearch': 'Clear search',
        'ost.modal.otherCategory': 'Other',
        'ost.modal.selectedCount': '{count} selected',
        'ost.modal.selectAll': 'Select all',
        'ost.modal.selectAllAria': 'Select all {count} {group} services',
        'ost.modal.clear': 'Clear',
        'ost.modal.clearGroupAria': 'Clear all {group} services',
        'ost.modal.inactiveBadge': 'inactive',
        'ost.modal.delete': 'Delete staff member',
        'ost.modal.err.fullName': 'Full name is required.',
        'ost.modal.err.email': 'Email is required.',
        'ost.modal.err.phone': 'Phone number is required.',

        /* Schedule tab */
        'ost.schedule.loadFailed': "Couldn't load the schedule.",
        'ost.schedule.noHours': 'No hours set',
        'ost.schedule.oneDaySet': '1 day set',
        'ost.schedule.daysSet': '{count} days set',
        'ost.schedule.hoursPerWeek': '{count} h/week',
        'ost.schedule.branchFallback': 'Branch #{id}',
        'ost.schedule.overlap': '{day}: {branchA} ({rangeA}) overlaps {branchB} ({rangeB})',
        'ost.schedule.endAfterStart': '{day}: end time must be after start time.',
        'ost.schedule.saved': 'Schedule saved for {name}.',
        'ost.schedule.saveFailed': "Couldn't save the schedule.",
        'ost.schedule.emptyTitle': 'No active staff yet',
        'ost.schedule.emptyBody': 'Add staff in the Staff list tab. Once they are active, you can set their working hours here.',
        'ost.schedule.pickerLabel': 'Manage schedule for',
        'ost.schedule.loading': 'Loading schedule…',
        'ost.schedule.loadError': "Couldn't load the schedule: {reason}",
        'ost.schedule.appliesTo': 'Hours apply to:',
        'ost.schedule.hqFull': 'Main branch',
        'ost.schedule.appliesToHint': 'To change which branches this staff member works at, edit them in the Staff list tab.',
        'ost.schedule.conflictTitle': "Schedule conflict — can't save",
        'ost.schedule.conflictBody': "A staff member can't be at two branches at the same time. Fix the overlaps below to save:",
        'ost.schedule.noBranches': "isn't assigned to a specific branch yet, so branch hours can't be set. Pick branches in the Staff list tab (Edit staff → Works at branches), then come back here.",
        'ost.schedule.expandAll': 'Expand all branches',
        'ost.schedule.collapseAll': 'Collapse all',
        'ost.schedule.dayToggleAria': '{day} at {branch} — working day',
        'ost.schedule.startAria': '{day}, {branch}: start time',
        'ost.schedule.endAria': '{day}, {branch}: end time',
        'ost.schedule.off': 'Off',
        'ost.schedule.applyMonday': "Apply Monday's hours to all working days",
        'ost.schedule.save': 'Save schedule',

        /* Appointment card menu */
        'ost.card.menu': 'Appointment actions',
        'ost.card.edit': 'Edit',
        'ost.card.viewPayment': 'View payment',
        'ost.card.delete': 'Delete',
        'ost.card.viewDetails': 'View details',
        'ost.card.reschedule': 'Reschedule',
        'ost.card.deleteConfirm': 'Delete this appointment?',
        'ost.card.cancel': 'Cancel',
        'ost.card.deleting': 'Deleting…',

        /* ── Payments ────────────────────────────────────────────────── */
        'opay.status.captured': 'Paid',
        'opay.status.paid': 'Paid',
        'opay.status.authorized': 'Authorised',
        'opay.status.partiallyRefunded': 'Partly refunded',
        'opay.status.cancelled': 'Cancelled',
        'opay.method.cash': 'Cash',
        'opay.method.card': 'Card',
        'opay.method.bankTransfer': 'Bank transfer',
        'opay.method.online': 'Online',
        'opay.method.other': 'Other',
        'opay.viewDetails': 'View details',
        'opay.col.rate': 'Rate',
        'opay.col.period': 'Period',
        'opay.filter.anyDate': 'Any date',
        'opay.totalCommission': 'Total commission',
        'opay.iban.label': 'Payout IBAN',
        'opay.iban.placeholder': 'Enter your IBAN',
        'opay.iban.update': 'Update IBAN',
        'opay.iban.updated': 'IBAN updated.',
        'opay.iban.updateFailed': "Couldn't update the IBAN.",
        'opay.card.setupFailed': "Couldn't start adding a card. Please try again.",
        'opay.card.added': 'Card added.',
        'opay.card.removed': 'Card removed.',
        'opay.card.removeFailed': "Couldn't remove the card.",
        'opay.card.methods': 'Payment methods',
        'opay.card.add': 'Add card',
        'opay.card.endingIn': 'ending in',
        'opay.card.expires': 'Expires',
        'opay.card.default': 'Default',
        'opay.card.remove': 'Remove',
        'opay.card.empty': 'No saved payment methods. Add a card to get started.',
        'opay.card.save': 'Save card',

        /* ── Settings ────────────────────────────────────────────────── */
        'oset.backToSettings': 'Back to Settings',
        'oset.loading': 'Loading…',
        'oset.saveChanges': 'Save changes',
        'oset.hours.allDay': '24 hours',
        'oset.hours.closed': 'Closed',
        'oset.hours.am': 'AM',
        'oset.hours.pm': 'PM',
        'oset.account.noBusiness': 'No business found to update.',
        'oset.account.nameRequired': 'Business name is required.',
        'oset.account.invalidHours': 'Please choose valid opening hours for {day}.',
        'oset.account.closeAfterOpen': 'Closing time must be after opening time for {day}.',
        'oset.account.saved': 'Business settings saved.',
        'oset.account.saveFailed': "Couldn't save business settings.",
        'oset.account.deleted': 'Business account deleted.',
        'oset.account.deleteFailed': "Couldn't delete the business account.",
        'oset.account.coverPhoto': 'Upload cover photo',
        'oset.account.logo': 'Logo',
        'oset.account.businessName': 'Business name',
        'oset.account.about': 'About',
        'oset.account.charCount': '{count} / {max} characters',
        'oset.account.businessHours': 'Business hours',
        'oset.account.alwaysOpen': 'Always open',
        'oset.account.location': 'Location',
        'oset.account.country': 'Country',
        'oset.account.state': 'State / emirate',
        'oset.account.street': 'Street address',
        'oset.account.city': 'City',
        'oset.account.zip': 'Postal code',
        'oset.account.homeService': 'Home service available',
        'oset.account.dangerZone': 'Delete business',
        'oset.account.dangerDesc': "Permanently delete your business account and all its data. This can't be undone.",
        'oset.account.deleteBusiness': 'Delete business account',
        'oset.account.businessAccount': 'business account',
        'oset.account.deleteConfirm': "Are you sure you want to delete your business account? This can't be undone.",
        'oset.profile.sessionExpired': 'Your session has expired. Please sign in again.',
        'oset.profile.loadFailed': "Couldn't load your profile. Please refresh the page or try again later.",
        'oset.profile.discardConfirm': 'You have unsaved changes. Discard them?',
        'oset.profile.genericError': 'Something went wrong. Please try again.',
        'oset.profile.err.nameRequired': 'Full name is required.',
        'oset.profile.err.nameChars': 'Full name must not contain <, > or &.',
        'oset.profile.err.nameMin': 'Full name must be at least 2 characters.',
        'oset.profile.err.nameMax': 'Full name must be at most 50 characters.',
        'oset.profile.err.emailRequired': 'Email is required.',
        'oset.profile.err.emailChars': 'Email must not contain <, > or &.',
        'oset.profile.err.emailInvalid': 'Please enter a valid email address.',
        'oset.profile.err.phone': 'Phone number must be 5–15 digits.',
        'oset.profile.err.countryCode': 'Enter a valid country code (e.g. +971).',
        'oset.profile.err.required': 'This field is required.',
        'oset.profile.err.passwordRules': 'Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol.',
        'oset.profile.err.passwordMismatch': "Passwords don't match.",
        'oset.profile.fixErrors': 'Please fix the highlighted fields before saving.',
        'oset.profile.saved': 'Profile updated.',
        'oset.profile.passwordChanged': 'Password changed.',
        'oset.profile.title': 'Profile information',
        'oset.profile.editTitle': 'Edit profile',
        'oset.profile.fullName': 'Full name',
        'oset.profile.namePlaceholder': 'Your full name',
        'oset.profile.phone': 'Phone',
        'oset.profile.password': 'Password',
        'oset.profile.countryCodeOptional': 'Country code (optional)',
        'oset.profile.phoneOptional': 'Phone number (optional)',
        'oset.profile.changePassword': 'Change password',
        'oset.profile.currentPassword': 'Current password',
        'oset.profile.newPassword': 'New password',
        'oset.profile.confirmPassword': 'Confirm new password',
        'oset.profile.savePassword': 'Save password',
        'oset.profile.strength': 'Password strength: {level}',
        'oset.profile.strength.weak': 'Weak',
        'oset.profile.strength.medium': 'Medium',
        'oset.profile.strength.strong': 'Strong',

        /* ── Notifications ───────────────────────────────────────────── */
        'onot.loading': 'Loading preferences…',
        'onot.save': 'Save preferences',
        'onot.toast.loadFailed': "Couldn't load notification preferences.",
        'onot.toast.saved': 'Notification preferences saved.',
        'onot.toast.saveFailed': "Couldn't save notification preferences.",
        'onot.push.sectionTitle': 'Browser notifications',
        'onot.push.title': 'Push notifications',
        'onot.push.desc': 'Get notified even when the Devanza tab is closed.',
        'onot.push.enabled': 'On',
        'onot.push.enable': 'Turn on',
        'onot.push.disable': 'Turn off',
        'onot.push.denied': 'Browser notifications are blocked. Allow them in your browser settings.',
        'onot.push.enabledToast': 'Push notifications turned on.',
        'onot.push.enableFailed': "Couldn't turn on push notifications.",
        'onot.push.disabledToast': 'Push notifications turned off.',
        'onot.push.disableFailed': "Couldn't turn off push notifications.",
        'onot.col.type': 'Notification',
        'onot.col.email': 'Email',
        'onot.col.inApp': 'In the app',
        'onot.col.push': 'Push',
        'onot.type.newAppointment.name': 'New bookings',
        'onot.type.newAppointment.desc': 'When a customer books an appointment.',
        'onot.type.appointmentCancelled.name': 'Cancelled bookings',
        'onot.type.appointmentCancelled.desc': 'When a booking is cancelled by the customer or the salon.',
        'onot.type.appointmentCompleted.name': 'Completed appointments',
        'onot.type.appointmentCompleted.desc': 'When an appointment is marked as completed.',
        'onot.type.newStaffJoined.name': 'New team members',
        'onot.type.newStaffJoined.desc': 'When a staff member accepts their invitation.',
        'onot.type.promotionExpiring.name': 'Promotions ending soon',
        'onot.type.promotionExpiring.desc': 'A reminder before one of your promotions ends.',
        'onot.type.system.name': 'Devanza updates',
        'onot.type.system.desc': 'Important account and service announcements from Devanza.',
        'onot.type.newBusinessCreated.name': 'New salons',
        'onot.type.newBusinessCreated.desc': 'When a new salon signs up on Devanza.',
        'onot.type.newUserRegistered.name': 'New user accounts',
        'onot.type.newUserRegistered.desc': 'When a new user creates an account.',
        'onot.type.onboardingApproved.name': 'Salon approved',
        'onot.type.onboardingApproved.desc': 'When a salon registration is approved.',
        'onot.type.onboardingRejected.name': 'Salon needs changes',
        'onot.type.onboardingRejected.desc': 'When a salon registration needs changes before approval.',
        'onot.type.onboardingSubmitted.name': 'Salon sent for review',
        'onot.type.onboardingSubmitted.desc': 'When a salon registration is submitted for review.',
        'onot.type.businessSuspended.name': 'Salon suspended',
        'onot.type.businessSuspended.desc': 'When a salon is suspended by Devanza.',
        'onot.type.businessReinstated.name': 'Salon reactivated',
        'onot.type.businessReinstated.desc': 'When a suspended salon is active again.',
        'onot.bell.aria': 'Notifications',
        'onot.bell.ariaUnread': 'Notifications ({count} unread)',
        'onot.bell.title': 'Notifications',
        'onot.bell.markAllRead': 'Mark all as read',
        'onot.bell.markReadNamed': 'Mark as read: {title}',
        'onot.bell.loading': 'Loading notifications…',
        'onot.bell.empty': 'No notifications yet',
        'onot.bell.clearConfirm': 'Clear all notifications?',
        'onot.bell.clearYes': 'Yes, clear all',
        'onot.bell.clearAll': 'Clear all',
        'onot.bell.delete': 'Delete notification',
        'onot.time.justNow': 'Just now',
        'onot.time.minutesAgo': '{count} min ago',
        'onot.time.hourAgo': '1 hour ago',
        'onot.time.hoursAgo': '{count} hours ago',
        'onot.time.dayAgo': '1 day ago',
        'onot.time.daysAgo': '{count} days ago',
    },
    ar: {
        /* ── Team / Manage Staff ─────────────────────────────────────── */
        'ost.title': 'إدارة الموظفين',
        'ost.subtitle': 'أضف فريقك — مصففين ومعالجين وموظفي استقبال، وكل من يستقبل الحجوزات أو لديه تقويم خاص.',
        'ost.tab.list': 'قائمة الفريق',
        'ost.tab.schedule': 'الجدول',
        'ost.tab.inactive': 'غير النشطين',
        'ost.tabs.aria': 'أقسام فريق العمل',
        'ost.empty.headline': 'لم تُضف أي عضو إلى فريق العمل بعد',
        'ost.empty.description': 'أضف الأشخاص الذين يعملون في صالونك في أي وقت. يمكنك أيضًا دعوتهم لإدارة حجوزاتهم بأنفسهم من تطبيق الجوال.',
        'ost.empty.cta': 'أضف أول موظف',
        'ost.empty.noMatchFilters': 'لا يوجد موظفون يطابقون عوامل التصفية.',
        'ost.empty.noMatchTab': 'لا يوجد موظفون في هذا القسم.',
        'ost.benefit.schedule.title': 'جدول خاص لكل موظف',
        'ost.benefit.schedule.desc': 'لكل موظف تقويمه وساعات عمله وخطة إجازاته.',
        'ost.benefit.services.title': 'تعيين الخدمات',
        'ost.benefit.services.desc': 'حدّد من يقدّم كل خدمة — ويختار العملاء المصفف المفضل لديهم.',
        'ost.benefit.app.title': 'تطبيق الموظفين',
        'ost.benefit.app.desc': 'ادعُهم إلى تطبيق Devanza للموظفين لإدارة حجوزاتهم من أي مكان.',
        'ost.error.load': 'تعذّر تحميل فريق العمل. حاول مرة أخرى.',
        'ost.error.noPermissionAdd': 'ليست لديك صلاحية إضافة موظفين.',
        'ost.error.saveServices': 'تعذّر حفظ خدمات هذا الموظف.',
        'ost.error.saveBranches': 'تعذّر حفظ فروع هذا الموظف.',
        'ost.error.save': 'تعذّر حفظ بيانات الموظف. حاول مرة أخرى.',
        'ost.error.delete': 'تعذّر حذف الموظف. حاول مرة أخرى.',
        'ost.toast.updated': 'تم تحديث بيانات الموظف.',
        'ost.toast.deleted': 'تم حذف الموظف.',
        'ost.staffFallbackName': 'موظف رقم {id}',
        'ost.role.manager': 'مدير',
        'ost.canPerform': 'يقدّم',
        'ost.capability.any': 'أي خدمة نشطة',
        'ost.capability.anyHint': 'لم تُحدَّد خدمات بعد، لذا يمكن حجز هذا الموظف لأي خدمة داخل الصالون.',
        'ost.capability.countOne': 'خدمة واحدة',
        'ost.capability.count': 'الخدمات: {count}',
        'ost.quickBook': 'حجز سريع +',
        'ost.action.edit': 'تعديل',
        'ost.action.editNamed': 'تعديل {name}',
        'ost.action.delete': 'حذف',
        'ost.action.deleteNamed': 'حذف {name}',
        'ost.count.staff': 'الموظفون: {count}',
        'ost.count.active': 'نشط: {count}',
        'ost.count.inactive': 'غير نشط: {count}',
        'ost.count.pending': 'بانتظار القبول: {count}',
        'ost.addStaff': '+ إضافة موظف',
        'ost.search.placeholder': 'ابحث بالاسم أو البريد الإلكتروني أو الهاتف…',
        'ost.search.aria': 'البحث في فريق العمل',
        'ost.filter.branch': 'الفرع',
        'ost.filter.branchAria': 'التصفية حسب الفرع',
        'ost.filter.all': 'الكل',
        'ost.filter.serviceAria': 'التصفية حسب الخدمة',
        'ost.filter.any': 'أي خدمة',
        'ost.filter.clearAll': 'مسح الكل',
        'ost.group.pending': 'دعوات بانتظار القبول',
        'ost.group.active': 'الفريق النشط',
        'ost.working': 'جارٍ التنفيذ…',
        'ost.delete.message': 'إذا كانت لدى هذا الموظف مواعيد قادمة، فستحتاج إلى إعادة تعيينها. لا يمكن التراجع عن هذا الإجراء.',
        'ost.invite.pending': 'بانتظار القبول',
        'ost.invite.expired': 'منتهية الصلاحية',
        'ost.invite.expiresInDay': 'تنتهي خلال يوم واحد',
        'ost.invite.expiresInDays': 'الأيام المتبقية: {count}',
        'ost.invite.expiresInHours': 'تنتهي خلال {count} س',
        'ost.invite.expiresSoon': 'تنتهي قريبًا',
        'ost.invite.copyLink': 'نسخ رابط الدعوة',
        'ost.invite.copyLinkFor': 'نسخ رابط الدعوة لـ {name}',
        'ost.invite.copied': 'تم النسخ',
        'ost.invite.linkCopied': 'تم نسخ رابط الدعوة',
        'ost.invite.copyFailed': 'تعذّر نسخ رابط الدعوة. حاول مرة أخرى.',
        'ost.invite.resend': 'إعادة الإرسال',
        'ost.invite.resendTo': 'إعادة إرسال الدعوة إلى {email}',
        'ost.invite.resent': 'تمت إعادة إرسال الدعوة إلى {email}.',
        'ost.invite.resendFailed': 'تعذّرت إعادة إرسال الدعوة.',
        'ost.invite.revoke': 'إلغاء الدعوة',
        'ost.invite.revokeTo': 'إلغاء الدعوة المرسلة إلى {email}',
        'ost.invite.revokeConfirm': 'هل تريد إلغاء الدعوة المرسلة إلى {email}؟ سيتوقف الرابط المرسل في البريد عن العمل.',
        'ost.invite.revoked': 'تم إلغاء الدعوة.',
        'ost.invite.revokeFailed': 'تعذّر إلغاء الدعوة.',

        'ost.day.0': 'الأحد',
        'ost.day.1': 'الاثنين',
        'ost.day.2': 'الثلاثاء',
        'ost.day.3': 'الأربعاء',
        'ost.day.4': 'الخميس',
        'ost.day.5': 'الجمعة',
        'ost.day.6': 'السبت',

        /* Staff modal */
        'ost.modal.close': 'إغلاق',
        'ost.modal.addTitle': 'إضافة موظف',
        'ost.modal.editTitle': 'تعديل بيانات الموظف',
        'ost.modal.editSubtitle': 'حدّث الاسم وبيانات التواصل والدور والخدمات التي يقدّمها.',
        'ost.modal.about': 'نبذة',
        'ost.modal.photo': 'الصورة الشخصية',
        'ost.modal.photoAddHint': 'يمكنك إضافة صورة بعد حفظ بيانات الموظف.',
        'ost.modal.changePhoto': 'تغيير الصورة',
        'ost.modal.uploadPhoto': 'رفع صورة',
        'ost.modal.photoFailed': 'تعذّر رفع الصورة. حاول مرة أخرى.',
        'ost.modal.photoHint': 'JPG أو PNG أو GIF بحجم أقصاه 5 MB. تظهر للعملاء في تطبيق الحجز.',
        'ost.modal.fullName': 'الاسم الكامل',
        'ost.modal.fullNamePlaceholder': 'مثال: سارة الهاشمي',
        'ost.modal.email': 'البريد الإلكتروني',
        'ost.modal.phone': 'رقم الهاتف',
        'ost.modal.position': 'المسمى الوظيفي',
        'ost.modal.positionPlaceholder': 'مصففة، معالجة، مديرة…',
        'ost.modal.role': 'الدور',
        'ost.modal.role.staff': 'موظف',
        'ost.modal.role.manager': 'مدير فريق العمل',
        'ost.modal.activeHint': 'الموظفون غير النشطين لا يظهرون للعملاء عند الحجز.',
        'ost.modal.whatTheyDo': 'مهامه',
        'ost.modal.worksAt': 'الفروع التي يعمل بها',
        'ost.modal.branchesNone': 'لم يُحدَّد فرع — متاح في جميع الفروع النشطة. اختر فروعًا لتحديد النطاق.',
        'ost.modal.branchesPickedOne': 'تم اختيار فرع واحد.',
        'ost.modal.branchesPicked': 'الفروع المختارة: {count}.',
        'ost.modal.hq': 'الرئيسي',
        'ost.modal.services': 'الخدمات التي يقدّمها هذا الموظف',
        'ost.modal.servicesNone': 'لم تُحدَّد خدمات — يمكن حجزه لأي خدمة داخل الصالون. اختر خدمات لتحديد النطاق.',
        'ost.modal.servicesNoneAllBranches': 'لم تُحدَّد خدمات — يمكن حجزه لأي خدمة في الفروع التي يعمل بها. اختر خدمات لتحديد النطاق.',
        'ost.modal.servicesNonePickedBranches': 'لم تُحدَّد خدمات — يمكن حجزه لأي خدمة في الفروع المختارة.',
        'ost.modal.servicesPickedOne': 'تم اختيار خدمة واحدة.',
        'ost.modal.servicesPicked': 'الخدمات المختارة: {count}.',
        'ost.modal.noServices': 'لا توجد خدمات بعد. أضف الخدمات من «منشأتي» ← «الخدمات»، ثم عُد لتعيينها.',
        'ost.modal.pickBranchFirst': 'اختر فرعًا من «الفروع التي يعمل بها» لعرض الخدمات المتاحة فيه.',
        'ost.modal.noServicesAtBranches': 'لا توجد خدمات في الفروع المختارة بعد. أضف الخدمات من «منشأتي» ← «الخدمات»، ثم عُد لتعيينها.',
        'ost.modal.searchServices': 'ابحث في الخدمات ({count})…',
        'ost.modal.searchServicesAria': 'البحث في الخدمات',
        'ost.modal.noMatch': 'لا توجد خدمات تطابق «{query}».',
        'ost.modal.clearSearch': 'مسح البحث',
        'ost.modal.otherCategory': 'أخرى',
        'ost.modal.selectedCount': 'المختارة: {count}',
        'ost.modal.selectAll': 'اختيار الكل',
        'ost.modal.selectAllAria': 'اختيار جميع خدمات {group} ({count})',
        'ost.modal.clear': 'مسح',
        'ost.modal.clearGroupAria': 'إلغاء اختيار جميع خدمات {group}',
        'ost.modal.inactiveBadge': 'غير نشطة',
        'ost.modal.delete': 'حذف الموظف',
        'ost.modal.err.fullName': 'الاسم الكامل مطلوب.',
        'ost.modal.err.email': 'البريد الإلكتروني مطلوب.',
        'ost.modal.err.phone': 'رقم الهاتف مطلوب.',

        /* Schedule tab */
        'ost.schedule.loadFailed': 'تعذّر تحميل الجدول.',
        'ost.schedule.noHours': 'لم تُحدَّد ساعات',
        'ost.schedule.oneDaySet': 'يوم واحد محدد',
        'ost.schedule.daysSet': 'الأيام المحددة: {count}',
        'ost.schedule.hoursPerWeek': '{count} ساعة/أسبوع',
        'ost.schedule.branchFallback': 'فرع رقم {id}',
        'ost.schedule.overlap': '{day}: {branchA} ({rangeA}) يتعارض مع {branchB} ({rangeB})',
        'ost.schedule.endAfterStart': '{day}: يجب أن يكون وقت الانتهاء بعد وقت البدء.',
        'ost.schedule.saved': 'تم حفظ جدول {name}.',
        'ost.schedule.saveFailed': 'تعذّر حفظ الجدول.',
        'ost.schedule.emptyTitle': 'لا يوجد موظفون نشطون بعد',
        'ost.schedule.emptyBody': 'أضف الموظفين من تبويب «قائمة الفريق». بعد تفعيلهم يمكنك تحديد ساعات عملهم هنا.',
        'ost.schedule.pickerLabel': 'إدارة جدول',
        'ost.schedule.loading': 'جارٍ تحميل الجدول…',
        'ost.schedule.loadError': 'تعذّر تحميل الجدول: {reason}',
        'ost.schedule.appliesTo': 'تنطبق الساعات على:',
        'ost.schedule.hqFull': 'الفرع الرئيسي',
        'ost.schedule.appliesToHint': 'لتغيير الفروع التي يعمل بها هذا الموظف، عدّلها من تبويب «قائمة الفريق».',
        'ost.schedule.conflictTitle': 'تعارض في الجدول — لا يمكن الحفظ',
        'ost.schedule.conflictBody': 'لا يمكن أن يكون الموظف في فرعين في الوقت نفسه. عالج التعارضات التالية للحفظ:',
        'ost.schedule.noBranches': 'غير معيَّن لفرع محدد بعد، لذا لا يمكن تحديد ساعات لكل فرع. اختر الفروع من تبويب «قائمة الفريق» («تعديل بيانات الموظف» ← «الفروع التي يعمل بها»)، ثم عُد إلى هنا.',
        'ost.schedule.expandAll': 'توسيع جميع الفروع',
        'ost.schedule.collapseAll': 'طي الكل',
        'ost.schedule.dayToggleAria': '{day} في {branch} — يوم عمل',
        'ost.schedule.startAria': '{day}، {branch}: وقت البدء',
        'ost.schedule.endAria': '{day}، {branch}: وقت الانتهاء',
        'ost.schedule.off': 'عطلة',
        'ost.schedule.applyMonday': 'تطبيق ساعات الاثنين على جميع أيام العمل',
        'ost.schedule.save': 'حفظ الجدول',

        /* Appointment card menu */
        'ost.card.menu': 'إجراءات الموعد',
        'ost.card.edit': 'تعديل',
        'ost.card.viewPayment': 'عرض الدفع',
        'ost.card.delete': 'حذف',
        'ost.card.viewDetails': 'عرض التفاصيل',
        'ost.card.reschedule': 'إعادة جدولة',
        'ost.card.deleteConfirm': 'هل تريد حذف هذا الموعد؟',
        'ost.card.cancel': 'إلغاء',
        'ost.card.deleting': 'جارٍ الحذف…',

        /* ── Payments ────────────────────────────────────────────────── */
        'opay.status.captured': 'مدفوع',
        'opay.status.paid': 'مدفوع',
        'opay.status.authorized': 'مُعتمد',
        'opay.status.partiallyRefunded': 'مسترد جزئيًا',
        'opay.status.cancelled': 'ملغى',
        'opay.method.cash': 'نقدًا',
        'opay.method.card': 'بطاقة',
        'opay.method.bankTransfer': 'تحويل بنكي',
        'opay.method.online': 'دفع إلكتروني',
        'opay.method.other': 'أخرى',
        'opay.viewDetails': 'عرض التفاصيل',
        'opay.col.rate': 'النسبة',
        'opay.col.period': 'الفترة',
        'opay.filter.anyDate': 'أي تاريخ',
        'opay.totalCommission': 'إجمالي العمولة',
        'opay.iban.label': 'رقم IBAN لاستلام المدفوعات',
        'opay.iban.placeholder': 'أدخل رقم IBAN',
        'opay.iban.update': 'تحديث IBAN',
        'opay.iban.updated': 'تم تحديث رقم IBAN.',
        'opay.iban.updateFailed': 'تعذّر تحديث رقم IBAN.',
        'opay.card.setupFailed': 'تعذّر بدء إضافة البطاقة. حاول مرة أخرى.',
        'opay.card.added': 'تمت إضافة البطاقة.',
        'opay.card.removed': 'تمت إزالة البطاقة.',
        'opay.card.removeFailed': 'تعذّرت إزالة البطاقة.',
        'opay.card.methods': 'طرق الدفع',
        'opay.card.add': 'إضافة بطاقة',
        'opay.card.endingIn': 'تنتهي بـ',
        'opay.card.expires': 'تنتهي صلاحيتها',
        'opay.card.default': 'الافتراضية',
        'opay.card.remove': 'إزالة',
        'opay.card.empty': 'لا توجد طرق دفع محفوظة. أضف بطاقة للبدء.',
        'opay.card.save': 'حفظ البطاقة',

        /* ── Settings ────────────────────────────────────────────────── */
        'oset.backToSettings': 'العودة إلى الإعدادات',
        'oset.loading': 'جارٍ التحميل…',
        'oset.saveChanges': 'حفظ التغييرات',
        'oset.hours.allDay': '24 ساعة',
        'oset.hours.closed': 'مغلق',
        'oset.hours.am': 'ص',
        'oset.hours.pm': 'م',
        'oset.account.noBusiness': 'لم يُعثر على منشأة لتحديثها.',
        'oset.account.nameRequired': 'اسم المنشأة مطلوب.',
        'oset.account.invalidHours': 'يُرجى اختيار ساعات عمل صحيحة ليوم {day}.',
        'oset.account.closeAfterOpen': 'يجب أن يكون وقت الإغلاق بعد وقت الفتح ليوم {day}.',
        'oset.account.saved': 'تم حفظ إعدادات المنشأة.',
        'oset.account.saveFailed': 'تعذّر حفظ إعدادات المنشأة.',
        'oset.account.deleted': 'تم حذف حساب المنشأة.',
        'oset.account.deleteFailed': 'تعذّر حذف حساب المنشأة.',
        'oset.account.coverPhoto': 'رفع صورة الغلاف',
        'oset.account.logo': 'الشعار',
        'oset.account.businessName': 'اسم المنشأة',
        'oset.account.about': 'نبذة',
        'oset.account.charCount': '{count} / {max} حرف',
        'oset.account.businessHours': 'ساعات العمل',
        'oset.account.alwaysOpen': 'مفتوح دائمًا',
        'oset.account.location': 'الموقع',
        'oset.account.country': 'الدولة',
        'oset.account.state': 'الإمارة / المحافظة',
        'oset.account.street': 'عنوان الشارع',
        'oset.account.city': 'المدينة',
        'oset.account.zip': 'الرمز البريدي',
        'oset.account.homeService': 'تتوفر خدمة منزلية',
        'oset.account.dangerZone': 'حذف المنشأة',
        'oset.account.dangerDesc': 'احذف حساب منشأتك وجميع بياناته نهائيًا. لا يمكن التراجع عن ذلك.',
        'oset.account.deleteBusiness': 'حذف حساب المنشأة',
        'oset.account.businessAccount': 'حساب المنشأة',
        'oset.account.deleteConfirm': 'هل أنت متأكد من حذف حساب منشأتك؟ لا يمكن التراجع عن ذلك.',
        'oset.profile.sessionExpired': 'انتهت جلستك. يُرجى تسجيل الدخول مجددًا.',
        'oset.profile.loadFailed': 'تعذّر تحميل ملفك الشخصي. حدّث الصفحة أو حاول لاحقًا.',
        'oset.profile.discardConfirm': 'لديك تغييرات غير محفوظة. هل تريد تجاهلها؟',
        'oset.profile.genericError': 'حدث خطأ ما. حاول مرة أخرى.',
        'oset.profile.err.nameRequired': 'الاسم الكامل مطلوب.',
        'oset.profile.err.nameChars': 'يجب ألا يحتوي الاسم على < أو > أو &.',
        'oset.profile.err.nameMin': 'يجب أن يتكوّن الاسم من حرفين على الأقل.',
        'oset.profile.err.nameMax': 'يجب ألا يتجاوز الاسم 50 حرفًا.',
        'oset.profile.err.emailRequired': 'البريد الإلكتروني مطلوب.',
        'oset.profile.err.emailChars': 'يجب ألا يحتوي البريد الإلكتروني على < أو > أو &.',
        'oset.profile.err.emailInvalid': 'يُرجى إدخال بريد إلكتروني صحيح.',
        'oset.profile.err.phone': 'يجب أن يتكوّن رقم الهاتف من 5 إلى 15 رقمًا.',
        'oset.profile.err.countryCode': 'أدخل رمز دولة صحيحًا (مثل ‎+971).',
        'oset.profile.err.required': 'هذا الحقل مطلوب.',
        'oset.profile.err.passwordRules': 'استخدم 8 أحرف على الأقل تتضمن حرفًا كبيرًا وحرفًا صغيرًا ورقمًا ورمزًا.',
        'oset.profile.err.passwordMismatch': 'كلمتا المرور غير متطابقتين.',
        'oset.profile.fixErrors': 'يُرجى تصحيح الحقول المحددة قبل الحفظ.',
        'oset.profile.saved': 'تم تحديث الملف الشخصي.',
        'oset.profile.passwordChanged': 'تم تغيير كلمة المرور.',
        'oset.profile.title': 'معلومات الملف الشخصي',
        'oset.profile.editTitle': 'تعديل الملف الشخصي',
        'oset.profile.fullName': 'الاسم الكامل',
        'oset.profile.namePlaceholder': 'اسمك الكامل',
        'oset.profile.phone': 'الهاتف',
        'oset.profile.password': 'كلمة المرور',
        'oset.profile.countryCodeOptional': 'رمز الدولة (اختياري)',
        'oset.profile.phoneOptional': 'رقم الهاتف (اختياري)',
        'oset.profile.changePassword': 'تغيير كلمة المرور',
        'oset.profile.currentPassword': 'كلمة المرور الحالية',
        'oset.profile.newPassword': 'كلمة المرور الجديدة',
        'oset.profile.confirmPassword': 'تأكيد كلمة المرور الجديدة',
        'oset.profile.savePassword': 'حفظ كلمة المرور',
        'oset.profile.strength': 'قوة كلمة المرور: {level}',
        'oset.profile.strength.weak': 'ضعيفة',
        'oset.profile.strength.medium': 'متوسطة',
        'oset.profile.strength.strong': 'قوية',

        /* ── Notifications ───────────────────────────────────────────── */
        'onot.loading': 'جارٍ تحميل التفضيلات…',
        'onot.save': 'حفظ التفضيلات',
        'onot.toast.loadFailed': 'تعذّر تحميل تفضيلات الإشعارات.',
        'onot.toast.saved': 'تم حفظ تفضيلات الإشعارات.',
        'onot.toast.saveFailed': 'تعذّر حفظ تفضيلات الإشعارات.',
        'onot.push.sectionTitle': 'إشعارات المتصفح',
        'onot.push.title': 'الإشعارات الفورية',
        'onot.push.desc': 'استلم الإشعارات حتى عند إغلاق صفحة Devanza.',
        'onot.push.enabled': 'مفعّلة',
        'onot.push.enable': 'تفعيل',
        'onot.push.disable': 'إيقاف',
        'onot.push.denied': 'إشعارات المتصفح محظورة. اسمح بها من إعدادات المتصفح.',
        'onot.push.enabledToast': 'تم تفعيل الإشعارات الفورية.',
        'onot.push.enableFailed': 'تعذّر تفعيل الإشعارات الفورية.',
        'onot.push.disabledToast': 'تم إيقاف الإشعارات الفورية.',
        'onot.push.disableFailed': 'تعذّر إيقاف الإشعارات الفورية.',
        'onot.col.type': 'الإشعار',
        'onot.col.email': 'البريد الإلكتروني',
        'onot.col.inApp': 'داخل التطبيق',
        'onot.col.push': 'فوري',
        'onot.type.newAppointment.name': 'حجوزات جديدة',
        'onot.type.newAppointment.desc': 'عندما يحجز عميل موعدًا.',
        'onot.type.appointmentCancelled.name': 'حجوزات ملغاة',
        'onot.type.appointmentCancelled.desc': 'عندما يُلغى حجز من قِبل العميل أو الصالون.',
        'onot.type.appointmentCompleted.name': 'مواعيد مكتملة',
        'onot.type.appointmentCompleted.desc': 'عندما يُسجَّل الموعد كمكتمل.',
        'onot.type.newStaffJoined.name': 'أعضاء جدد في الفريق',
        'onot.type.newStaffJoined.desc': 'عندما يقبل موظف دعوته.',
        'onot.type.promotionExpiring.name': 'عروض قاربت على الانتهاء',
        'onot.type.promotionExpiring.desc': 'تذكير قبل انتهاء أحد عروضك.',
        'onot.type.system.name': 'تحديثات Devanza',
        'onot.type.system.desc': 'إعلانات مهمة من Devanza حول حسابك والخدمة.',
        'onot.type.newBusinessCreated.name': 'صالونات جديدة',
        'onot.type.newBusinessCreated.desc': 'عندما يسجّل صالون جديد في Devanza.',
        'onot.type.newUserRegistered.name': 'حسابات مستخدمين جديدة',
        'onot.type.newUserRegistered.desc': 'عندما ينشئ مستخدم جديد حسابًا.',
        'onot.type.onboardingApproved.name': 'اعتماد الصالون',
        'onot.type.onboardingApproved.desc': 'عندما يُعتمد تسجيل صالون.',
        'onot.type.onboardingRejected.name': 'الصالون بحاجة إلى تعديلات',
        'onot.type.onboardingRejected.desc': 'عندما يحتاج تسجيل الصالون إلى تعديلات قبل اعتماده.',
        'onot.type.onboardingSubmitted.name': 'إرسال الصالون للمراجعة',
        'onot.type.onboardingSubmitted.desc': 'عندما يُرسل تسجيل صالون للمراجعة.',
        'onot.type.businessSuspended.name': 'إيقاف الصالون',
        'onot.type.businessSuspended.desc': 'عندما توقف Devanza صالونًا.',
        'onot.type.businessReinstated.name': 'إعادة تفعيل الصالون',
        'onot.type.businessReinstated.desc': 'عندما يعود صالون موقوف إلى العمل.',
        'onot.bell.aria': 'الإشعارات',
        'onot.bell.ariaUnread': 'الإشعارات ({count} غير مقروءة)',
        'onot.bell.title': 'الإشعارات',
        'onot.bell.markAllRead': 'تحديد الكل كمقروء',
        'onot.bell.markReadNamed': 'تحديد كمقروء: {title}',
        'onot.bell.loading': 'جارٍ تحميل الإشعارات…',
        'onot.bell.empty': 'لا توجد إشعارات بعد',
        'onot.bell.clearConfirm': 'هل تريد مسح جميع الإشعارات؟',
        'onot.bell.clearYes': 'نعم، امسح الكل',
        'onot.bell.clearAll': 'مسح الكل',
        'onot.bell.delete': 'حذف الإشعار',
        'onot.time.justNow': 'الآن',
        'onot.time.minutesAgo': 'منذ {count} د',
        'onot.time.hourAgo': 'منذ ساعة',
        'onot.time.hoursAgo': 'منذ {count} س',
        'onot.time.dayAgo': 'منذ يوم',
        'onot.time.daysAgo': 'منذ {count} أيام',
    },
};
